import { useState } from 'react';
import { Code, Copy, Check, Globe, MousePointer, ShoppingBag, Maximize2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CodeBlock = ({ code, language = 'html' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-[#1a1a1a] text-green-300 rounded-lg p-4 overflow-x-auto text-xs leading-relaxed font-mono">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-3 right-3 p-1.5 rounded bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors"
        data-testid="copy-code-btn"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

const AdminEmbedGuide = () => {
  const embedUrl = `${BACKEND_URL}/embed`;
  const quizUrl = `${BACKEND_URL}/quiz`;
  const fullUrl = BACKEND_URL;

  const tabs = [
    { id: 'page', label: 'Dedicated Page', icon: Globe },
    { id: 'button', label: 'Homepage Button', icon: MousePointer },
    { id: 'product', label: 'Product Pages', icon: ShoppingBag },
    { id: 'popup', label: 'Popup Trigger', icon: Maximize2 },
  ];

  const [activeTab, setActiveTab] = useState('page');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Shopify Embedding Guide</h1>
        <p className="text-stone-600 text-sm">Step-by-step instructions to add the Supplement Finder to your Shopify store</p>
      </div>

      {/* Live URLs */}
      <Card>
        <CardContent className="p-6">
          <p className="text-sm font-medium text-stone-700 mb-3">Your Supplement Finder URLs</p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 bg-stone-50 rounded-lg p-3">
              <span className="text-xs text-stone-500 w-24 flex-shrink-0">Full App:</span>
              <code className="text-xs text-[#4A6763] font-mono break-all" data-testid="full-app-url">{fullUrl}</code>
            </div>
            <div className="flex items-center gap-3 bg-stone-50 rounded-lg p-3">
              <span className="text-xs text-stone-500 w-24 flex-shrink-0">Embed URL:</span>
              <code className="text-xs text-[#4A6763] font-mono break-all" data-testid="embed-url">{embedUrl}</code>
            </div>
            <div className="flex items-center gap-3 bg-stone-50 rounded-lg p-3">
              <span className="text-xs text-stone-500 w-24 flex-shrink-0">Quiz Direct:</span>
              <code className="text-xs text-[#4A6763] font-mono break-all" data-testid="quiz-url">{quizUrl}</code>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-[#1a1a1a] text-white'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" strokeWidth={1.5} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'page' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-serif flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Dedicated Page: "Find My Supplements"
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-medium text-stone-800 mb-2">Step 1: Create the Page in Shopify</h3>
                <ol className="text-sm text-stone-600 space-y-2 list-decimal ml-5">
                  <li>Go to <strong>Shopify Admin &rarr; Online Store &rarr; Pages</strong></li>
                  <li>Click <strong>"Add page"</strong></li>
                  <li>Set the title to <strong>"Find My Supplements"</strong></li>
                  <li>In the page editor, click the <strong>&lt;/&gt; (Show HTML)</strong> button</li>
                  <li>Paste the code below into the HTML editor</li>
                  <li>Click <strong>Save</strong></li>
                </ol>
              </div>

              <CodeBlock code={`<div id="supplement-finder-container" style="width:100%; min-height:700px;">
  <iframe
    src="${embedUrl}?source=shopify-page"
    style="width:100%; height:85vh; min-height:700px; border:none; border-radius:8px;"
    title="Find My Supplements"
    allow="clipboard-write"
    loading="lazy"
  ></iframe>
</div>`} />

              <div>
                <h3 className="font-medium text-stone-800 mb-2">Step 2: Add to Navigation</h3>
                <ol className="text-sm text-stone-600 space-y-2 list-decimal ml-5">
                  <li>Go to <strong>Online Store &rarr; Navigation</strong></li>
                  <li>Click on your <strong>Main menu</strong></li>
                  <li>Click <strong>"Add menu item"</strong></li>
                  <li>Name: <strong>"Find My Supplements"</strong></li>
                  <li>Link: Select <strong>Pages &rarr; Find My Supplements</strong></li>
                  <li>Click <strong>Save</strong></li>
                </ol>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-medium mb-1">Pro Tip</p>
                <p className="text-xs text-amber-700">
                  For the best experience, use a <strong>full-width page template</strong> if your theme supports it. 
                  This removes the sidebar and gives the quiz more room. In most Shopify themes, you can select 
                  the template under <strong>Theme template &rarr; page.full-width</strong> at the bottom of the page editor.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'button' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-serif flex items-center gap-2">
                <MousePointer className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Homepage Button: Launch Quiz
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-medium text-stone-800 mb-2">Option A: Link to Quiz Page</h3>
                <p className="text-sm text-stone-600 mb-3">
                  The simplest method. Add a button anywhere on your homepage that links to your dedicated quiz page or directly to the quiz.
                </p>
              </div>

              <CodeBlock code={`<!-- Link to your "Find My Supplements" Shopify page -->
<a href="/pages/find-my-supplements" 
   class="button button--primary"
   style="display:inline-block; background:#1a1a1a; color:#fff; 
          padding:14px 32px; text-decoration:none; border-radius:4px; 
          font-size:14px; font-weight:500; letter-spacing:0.5px;">
  Find My Supplements
</a>`} />

              <div>
                <h3 className="font-medium text-stone-800 mb-2">Option B: Open Quiz in New Tab</h3>
                <p className="text-sm text-stone-600 mb-3">
                  Opens the full quiz experience in a new browser tab.
                </p>
              </div>

              <CodeBlock code={`<a href="${quizUrl}?source=shopify-homepage" 
   target="_blank"
   rel="noopener"
   class="button button--primary"
   style="display:inline-block; background:#1a1a1a; color:#fff; 
          padding:14px 32px; text-decoration:none; border-radius:4px; 
          font-size:14px; font-weight:500; letter-spacing:0.5px;">
  Take the Supplement Quiz
</a>`} />

              <div>
                <h3 className="font-medium text-stone-800 mb-2">Where to Add This</h3>
                <ol className="text-sm text-stone-600 space-y-2 list-decimal ml-5">
                  <li>Go to <strong>Online Store &rarr; Themes &rarr; Customize</strong></li>
                  <li>On the homepage, click <strong>"Add section"</strong></li>
                  <li>Choose <strong>"Custom Liquid"</strong> or <strong>"Rich text"</strong> section</li>
                  <li>Paste the button code</li>
                  <li>Drag the section to your desired position on the page</li>
                  <li>Click <strong>Save</strong></li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'product' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-serif flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Product Pages: "Not Sure Which Supplement?"
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm text-stone-600 mb-3">
                  Add a call-to-action banner on product pages to guide undecided shoppers to the quiz.
                </p>
              </div>

              <CodeBlock code={`<!-- Add to product page via Customize > Product page > Add block > Custom Liquid -->
<div style="background:#EFF3E1; border-radius:8px; padding:24px; 
            text-align:center; margin:24px 0;">
  <p style="font-size:16px; color:#1a1a1a; margin-bottom:4px; font-weight:500;">
    Not sure which supplement is right for you?
  </p>
  <p style="font-size:13px; color:#666; margin-bottom:16px;">
    Take our 2-minute quiz for a personalized recommendation
  </p>
  <a href="/pages/find-my-supplements"
     style="display:inline-block; background:#1a1a1a; color:#fff; 
            padding:12px 28px; text-decoration:none; border-radius:4px; 
            font-size:13px; font-weight:500; letter-spacing:0.5px;">
    Find My Supplements
  </a>
</div>`} />

              <div>
                <h3 className="font-medium text-stone-800 mb-2">How to Add to Product Pages</h3>
                <ol className="text-sm text-stone-600 space-y-2 list-decimal ml-5">
                  <li>Go to <strong>Online Store &rarr; Themes &rarr; Customize</strong></li>
                  <li>Navigate to a <strong>Product page</strong> using the page selector at the top</li>
                  <li>In the product template, click <strong>"Add block"</strong> under the product info section</li>
                  <li>Select <strong>"Custom Liquid"</strong></li>
                  <li>Paste the code above</li>
                  <li>Drag the block below the "Add to Cart" button</li>
                  <li>Click <strong>Save</strong> — this will appear on ALL product pages</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'popup' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-serif flex items-center gap-2">
                <Maximize2 className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Popup Trigger: Modal Quiz
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm text-stone-600 mb-3">
                  Launch the quiz as a fullscreen popup overlay. Users can take the quiz without leaving the current page.
                </p>
              </div>

              <CodeBlock code={`<!-- Popup Trigger Button (place anywhere on any page) -->
<button onclick="openSupplementQuiz()" 
        style="background:#1a1a1a; color:#fff; padding:14px 32px; 
               border:none; border-radius:4px; font-size:14px; 
               font-weight:500; letter-spacing:0.5px; cursor:pointer;">
  Find My Supplements
</button>

<!-- Popup Modal (add once, before </body> in theme.liquid) -->
<div id="supplement-quiz-modal" 
     style="display:none; position:fixed; inset:0; z-index:9999; 
            background:rgba(0,0,0,0.6); backdrop-filter:blur(4px);">
  <div style="position:absolute; inset:16px; max-width:900px; margin:auto; 
              background:#fff; border-radius:12px; overflow:hidden; 
              display:flex; flex-direction:column;">
    <div style="display:flex; justify-content:space-between; align-items:center; 
                padding:12px 16px; border-bottom:1px solid #eee;">
      <span style="font-size:14px; font-weight:500; color:#1a1a1a;">
        Find My Supplements
      </span>
      <button onclick="closeSupplementQuiz()" 
              style="background:none; border:none; font-size:24px; 
                     cursor:pointer; color:#666; line-height:1;">
        &times;
      </button>
    </div>
    <iframe id="supplement-quiz-iframe"
            src="" 
            style="flex:1; border:none; width:100%;"
            title="Supplement Finder Quiz">
    </iframe>
  </div>
</div>

<script>
  function openSupplementQuiz() {
    var modal = document.getElementById('supplement-quiz-modal');
    var iframe = document.getElementById('supplement-quiz-iframe');
    iframe.src = '${embedUrl}?source=shopify-popup';
    modal.style.display = 'block';
    document.body.style.overflow = 'hidden';
  }
  function closeSupplementQuiz() {
    var modal = document.getElementById('supplement-quiz-modal');
    var iframe = document.getElementById('supplement-quiz-iframe');
    modal.style.display = 'none';
    iframe.src = '';
    document.body.style.overflow = '';
  }
  // Close on background click
  document.getElementById('supplement-quiz-modal')
    .addEventListener('click', function(e) {
      if (e.target === this) closeSupplementQuiz();
    });
  // Close on Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') closeSupplementQuiz();
  });
</script>`} />

              <div>
                <h3 className="font-medium text-stone-800 mb-2">How to Install</h3>
                <ol className="text-sm text-stone-600 space-y-2 list-decimal ml-5">
                  <li>Go to <strong>Online Store &rarr; Themes &rarr; Actions &rarr; Edit code</strong></li>
                  <li>Open <strong>Layout &rarr; theme.liquid</strong></li>
                  <li>Find the closing <code className="bg-stone-100 px-1 rounded">&lt;/body&gt;</code> tag</li>
                  <li>Paste the <strong>modal HTML + script</strong> just <strong>above</strong> <code className="bg-stone-100 px-1 rounded">&lt;/body&gt;</code></li>
                  <li>Click <strong>Save</strong></li>
                  <li>Now add the <strong>trigger button</strong> anywhere on your site using Custom Liquid sections</li>
                </ol>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-medium mb-1">Advanced: Auto-Trigger Popup</p>
                <p className="text-xs text-amber-700">
                  To automatically show the popup after a delay (e.g. 8 seconds), add this script to your theme.liquid:
                </p>
                <pre className="mt-2 bg-[#1a1a1a] text-green-300 rounded p-2 text-xs font-mono overflow-x-auto">{`<script>
  setTimeout(function() {
    if (!sessionStorage.getItem('quiz_shown')) {
      openSupplementQuiz();
      sessionStorage.setItem('quiz_shown', 'true');
    }
  }, 8000);
</script>`}</pre>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* General Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif">Important Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-stone-600 space-y-3 list-disc ml-5">
            <li>
              <strong>Analytics tracking:</strong> Each embed method includes a <code className="bg-stone-100 px-1 rounded text-xs">source</code> parameter 
              so you can see in the Analytics tab where quiz completions come from (page, homepage, popup, etc.)
            </li>
            <li>
              <strong>Mobile responsive:</strong> The quiz is fully mobile-responsive and works well in iframes on all screen sizes.
            </li>
            <li>
              <strong>Cart integration:</strong> When users click "Add to Cart" on quiz results, they are redirected to your Shopify checkout with the selected items pre-loaded.
            </li>
            <li>
              <strong>Custom domain:</strong> After deployment to your production domain, replace the embed URLs above with your production URL.
            </li>
            <li>
              <strong>Theme compatibility:</strong> These code snippets work with all Shopify themes (Dawn, Debut, etc.). If you use a custom theme, the Custom Liquid section name may vary.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmbedGuide;
